// WALLET-P1 §6.1: Setup › Company › Billing (`/c/{slug}/setup/billing`): the company's prepaid
// AI credit. Owners and admins add funds and set the warning level; an agency operator reads
// (they credit by adjustment in Operator › Billing). Stats (balance, spent this month, the
// warning level, auto-reload), Spend by agent / task / day, the History (cursor-paginated like
// Setup › Costs) and what things cost. Money in dollars; tokens only in the detail drawer.
// BILLING-PLANS §6.1: below the credit, the Plan area (plan, agents' maintenance, next invoice,
// invoices), shown whatever the wallet's state.
import {
  Button,
  buttonVariants,
  cn,
  EmptyState,
  InfoTip,
  Notice,
  StatGrid,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLinkIcon, HistoryIcon, PanelRightOpenIcon, WalletIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isOperatorOnly } from "../company";
import { formatDate } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { AddFundsButton } from "./add-funds-dialog";
import { balanceText, bpText, dollars, rateText, signedDollars } from "./billing-money";
import {
  billingKeys,
  checkoutQuery,
  HISTORY_PAGE,
  WALLET_POLL_MS,
  type WalletLine,
  type WalletView,
  walletQuery,
} from "./billing-queries";
import { type DrawerSubject, EntryDrawer } from "./entry-drawer";
import { HelperQuotes } from "./helper-quotes";
import { PlanArea } from "./plan/plan-area";
import { SpendTabs } from "./spend-tabs";
import { WarningLevelDialog } from "./warning-dialog";

export function BillingPage({
  api,
  company,
  topup,
  onTopupSeen,
}: {
  api: Api;
  company: CompanyAccess;
  /** `?topup=` on the way back from Checkout: a checkout id, or `cancelled`. */
  topup?: string | undefined;
  /** Strips `?topup=` from the URL once the page has said what happened. */
  onTopupSeen?: () => void;
}) {
  const wallet = useQuery({ ...walletQuery(api, company.id), refetchInterval: WALLET_POLL_MS });
  const operatorOnly = isOperatorOnly(company);
  const [subject, setSubject] = useState<DrawerSubject | null>(null);
  const processing = useTopupReturn(api, company, topup, onTopupSeen);
  const w = wallet.data;
  const canPay = !operatorOnly && w?.enabled === true;
  // A wallet that is off but has history stays readable; only one with no financial activity
  // (balance 0, no entries) gets the empty state.
  const off = w !== undefined && !w.enabled;
  const peek = useQuery({
    queryKey: [...billingKeys.entries(company.id), "peek"],
    queryFn: () => api.client.wallet.entries({ params: { co: company.id }, query: { limit: 1 } }),
    enabled: off,
    refetchInterval: WALLET_POLL_MS,
  });
  const hasHistory = off && (w.balance_micros !== 0 || (peek.data?.data.length ?? 0) > 0);
  return (
    <SetupPage
      title="Billing"
      width="full"
      slot="billing-page"
      actions={canPay && w ? <AddFundsButton api={api} company={company} wallet={w} /> : null}
    >
      <div className="flex min-w-0 flex-col gap-6 px-6 py-5 max-md:px-4 max-md:py-4">
        {/* SKILLSMITH-OPPORTUNITIES: helper quotes waiting on the owner come first (hidden if none). */}
        <HelperQuotes api={api} companyId={company.id} />
        {wallet.isPending ? (
          <Loading label="Loading billing" rows={4} />
        ) : wallet.isError ? (
          <LoadError what="Billing" error={wallet.error} />
        ) : off && peek.isPending ? (
          <Loading label="Loading billing" rows={4} />
        ) : off && peek.isError && w.balance_micros === 0 ? (
          <div className="flex flex-col items-start gap-2" data-slot="wallet-history-error">
            <LoadError what="Billing history" error={peek.error} />
            <Button variant="outline" size="sm" onClick={() => void peek.refetch()}>
              Retry
            </Button>
          </div>
        ) : off && !hasHistory ? (
          <EmptyState
            variant="card"
            icon={WalletIcon}
            title="Credit isn't switched on for this company"
            data-slot="wallet-off"
          />
        ) : (
          <>
            {off ? (
              <p
                className="inline-flex items-center gap-1 text-sm font-medium"
                data-slot="wallet-off-label"
              >
                Wallet off
                <InfoTip label="About wallet off">
                  Credit is switched off for this company. Past balance, spend and receipts stay
                  readable; adding funds and settings are unavailable.
                </InfoTip>
              </p>
            ) : null}
            {processing ? (
              <Notice tone="info" data-slot="topup-processing">
                Payment processing…
              </Notice>
            ) : null}
            {wallet.data.paused && !off ? (
              <Notice
                tone="danger"
                data-slot="wallet-paused"
                action={
                  canPay ? (
                    <AddFundsButton api={api} company={company} wallet={wallet.data} />
                  ) : null
                }
              >
                Agents paused: no credit
              </Notice>
            ) : null}
            <WalletStats
              api={api}
              company={company}
              wallet={wallet.data}
              canEdit={!operatorOnly && !off}
            />
            <SpendTabs
              api={api}
              company={company}
              onOpen={(group, period, row) => setSubject({ type: "spend", group, period, row })}
            />
            <History
              api={api}
              company={company}
              onOpen={(line) => setSubject({ type: "line", line })}
            />
            <WhatThingsCost wallet={wallet.data} />
          </>
        )}
        {/* BILLING-PLANS §6.1: the plan, agents' maintenance, the next invoice and invoices */}
        <PlanArea api={api} company={company} />
      </div>
      <EntryDrawer api={api} company={company} subject={subject} onClose={() => setSubject(null)} />
    </SetupPage>
  );
}

/**
 * Back from Checkout: asks `wallet.get?checkout=<id>` whether the webhook credited that top-up,
 * polling while it is still open (about 30 s), says what happened and strips `?topup=`. Returns
 * true while it waits. "Funds added" is only said for a `paid` top-up.
 */
function useTopupReturn(
  api: Api,
  company: CompanyAccess,
  topup: string | undefined,
  onSeen: (() => void) | undefined,
): boolean {
  const qc = useQueryClient();
  const [processing, setProcessing] = useState(false);
  const seenRef = useRef(onSeen);
  seenRef.current = onSeen;
  useEffect(() => {
    if (!topup) return;
    if (topup === "cancelled") {
      toast.add({ title: "No funds added" });
      seenRef.current?.();
      return;
    }
    let stop = false;
    setProcessing(true);
    void (async () => {
      let checkout: WalletView["checkout"] = null;
      let failed = false;
      for (let i = 0; i < TOPUP_POLLS && !stop; i++) {
        try {
          checkout = (
            await qc.fetchQuery({ ...checkoutQuery(api, company.id, topup), staleTime: 0 })
          ).checkout;
          failed = false;
        } catch {
          failed = true;
        }
        // Unknown to this company, or credited: nothing more to wait for.
        if (!failed && (checkout === null || checkout.status !== "open")) break;
        if (i < TOPUP_POLLS - 1) await new Promise((r) => setTimeout(r, TOPUP_POLL_MS));
      }
      if (stop) return;
      setProcessing(false);
      if (checkout?.status === "paid") {
        toast.add({
          title: `Funds added: ${dollars(checkout.amount_micros)}${checkout.bonus_micros > 0 ? ` + ${dollars(checkout.bonus_micros)} bonus` : ""}`,
          type: "success",
        });
      } else if (checkout?.status === "open") {
        toast.add({
          title: "Payment still processing",
          description: "Funds appear once the payment clears. Check back in a few minutes.",
        });
      } else if (checkout?.status === "expired") {
        toast.add({ title: "Payment expired", description: "No funds were added." });
      } else {
        toast.add({
          title: failed ? "Couldn't confirm the payment" : "Payment not found",
          description: "No funds were added yet. Check the history below.",
        });
      }
      await qc.invalidateQueries({ queryKey: billingKeys.all(company.id) });
      seenRef.current?.();
    })();
    return () => {
      stop = true;
    };
  }, [api, company.id, topup, qc]);
  return processing;
}

/** Poll `wallet.get?checkout=` every 2 s, up to about 30 s. */
const TOPUP_POLL_MS = 2000;
const TOPUP_POLLS = 15;

const TILE =
  "flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3 text-card-foreground";

function Stat({
  label,
  value,
  detail,
  info,
  action,
  slot,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  info?: ReactNode;
  action?: ReactNode;
  slot: string;
}) {
  return (
    <div className={cn(TILE, "group/row")} data-slot={slot}>
      <div className="flex min-h-5 items-center justify-between gap-2">
        <span className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
          <span className="truncate">{label}</span>
          {info ? <InfoTip label={`About ${label}`}>{info}</InfoTip> : null}
        </span>
        {action ? <RowActions>{action}</RowActions> : null}
      </div>
      <span className="text-xl font-semibold tabular-nums">{value}</span>
      {detail ? <span className="text-xs text-muted-foreground">{detail}</span> : null}
    </div>
  );
}

function WalletStats({
  api,
  company,
  wallet,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  wallet: WalletView;
  canEdit: boolean;
}) {
  return (
    <StatGrid dense aria-label="Credit" data-slot="wallet-stats">
      <Stat
        slot="stat-balance"
        label="Balance"
        value={balanceText(wallet.balance_micros)}
        {...(wallet.held_micros > 0
          ? { detail: `${dollars(wallet.held_micros)} held by running work` }
          : {})}
      />
      <Stat
        slot="stat-spent"
        label="Spent this month"
        value={dollars(wallet.spent_this_month_micros)}
      />
      <Stat
        slot="stat-warning"
        label="Low-balance warning"
        value={dollars(wallet.low_threshold_micros)}
        detail={
          wallet.last_topup_micros !== null
            ? `${bpText(wallet.low_watermark_bp)} of last top-up`
            : `${bpText(wallet.low_watermark_bp)} of each top-up`
        }
        info="You are warned when credit drops below this."
        {...(canEdit
          ? { action: <WarningLevelDialog api={api} company={company} wallet={wallet} /> }
          : {})}
      />
      <Stat
        slot="stat-auto-reload"
        label="Auto-reload"
        value="Off"
        info="Automatic top-ups come later."
      />
    </StatGrid>
  );
}

function History({
  api,
  company,
  onOpen,
}: {
  api: Api;
  company: CompanyAccess;
  onOpen: (line: WalletLine) => void;
}) {
  const entries = useInfiniteQuery({
    queryKey: billingKeys.entries(company.id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.wallet.entries({
        params: { co: company.id },
        query: { limit: HISTORY_PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: WALLET_POLL_MS,
  });
  const rows = entries.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <section aria-labelledby="billing-history" className="flex min-w-0 flex-col gap-2">
      <h2 id="billing-history" className="text-sm font-semibold">
        History
      </h2>
      {entries.isPending ? (
        <Loading label="Loading history" rows={3} />
      ) : entries.isError ? (
        <LoadError what="History" error={entries.error} />
      ) : rows.length === 0 ? (
        <EmptyState variant="card" icon={HistoryIcon} title="No credit movements yet" />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <Table className={DENSE_TABLE} data-slot="wallet-history">
            <TableCaption className="sr-only">Newest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Date</TableHead>
                <TableHead scope="col">What</TableHead>
                <TableHead scope="col" className="text-right">
                  Amount
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Balance after
                </TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((l) => (
                <TableRow key={l.id} data-line={l.id} data-kind={l.kind} className="group/row">
                  <TableCell className="text-muted-foreground tabular-nums">
                    {formatDate(l.occurred_at)}
                  </TableCell>
                  <TableHead scope="row" className="max-w-96 truncate font-normal">
                    {l.label}
                  </TableHead>
                  <TableCell
                    className={cn(
                      "text-right tabular-nums",
                      l.amount_micros > 0 && "text-success-text",
                    )}
                  >
                    {signedDollars(l.amount_micros)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground max-md:hidden">
                    {balanceText(l.balance_after_micros)}
                  </TableCell>
                  <TableCell className="w-px text-right">
                    <RowActions>
                      {l.receipt_url ? (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <a
                                href={l.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Receipt for ${l.label}`}
                                className={cn(
                                  buttonVariants({ variant: "ghost", size: "icon-sm" }),
                                  "text-muted-foreground hover:text-foreground",
                                )}
                              />
                            }
                          >
                            <ExternalLinkIcon aria-hidden="true" />
                          </TooltipTrigger>
                          <TooltipContent>Receipt</TooltipContent>
                        </Tooltip>
                      ) : null}
                      <RowAction
                        icon={PanelRightOpenIcon}
                        label={`Details of ${l.label}`}
                        tip="Details"
                        onClick={() => onOpen(l)}
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <LoadMore
            what="entries"
            hasMore={entries.hasNextPage}
            loading={entries.isFetchingNextPage}
            onMore={() => void entries.fetchNextPage()}
          />
        </div>
      )}
    </section>
  );
}

function WhatThingsCost({ wallet }: { wallet: WalletView }) {
  const t = wallet.tariff;
  if (!t) return null;
  const rows: [string, number][] = [
    ["Input, per 1M tokens", t.input_per_m_micros],
    ["Output, per 1M tokens", t.output_per_m_micros],
    ["Per image", t.image_micros],
    // Per-unit prices: video analysed, audio transcribed, speech, web search, …
    ...t.unit_prices.map((u): [string, number] => [`${u.label}, per ${u.per}`, u.price_micros]),
  ];
  return (
    <section
      aria-labelledby="billing-prices"
      data-slot="what-things-cost"
      className="flex max-w-md flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="flex items-center gap-1">
        <h2 id="billing-prices" className="text-sm font-semibold">
          What things cost
        </h2>
        <InfoTip label="About tokens">
          A token is a small piece of text, about three quarters of a word.
        </InfoTip>
      </div>
      <dl className="flex flex-col gap-1 text-sm">
        {rows.map(([label, micros]) => (
          <div key={label} className="flex justify-between gap-4">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{rateText(micros)}</dd>
          </div>
        ))}
      </dl>
      <a href="/privacy#sub-processors" className="self-start text-xs text-primary hover:underline">
        Sub-processors
      </a>
    </section>
  );
}
